import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, CalendarRange, Calculator, CheckCircle2, ClipboardList, FileSignature, Home, MessageSquareWarning, Send, ThumbsDown, ThumbsUp, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtDateTime, fmtINR } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useMasterOptions } from "../collector/FieldForms";
import { SafFloorEditor, floorsPayload, newSafFloor, isFloorFilled } from "../../components/saf/SafFloorEditor";
import { SafTaxEstimate, SafTaxTable } from "../../components/saf/SafTaxEstimate";
import { SafStatusBadge, SafTimeline, SafEscalationTags } from "../../components/saf/SafStatus";

/**
 * Phase 207 / 209 — Citizen Self Assessment Form (SAF).
 *  1. Property details (owner, location, Road width — needed by the rate engine).
 *  2. Floor-wise assessment — every row has the same fields as Admin → Manual Entry (Floor, House No, Property type,
 *     Construction type, Usage, Sub-usage, Category, Sub-category, Plot / Open / Constructed / Carpet, Electricity CA No,
 *     Construction Date, Construction Age). Property type is captured per floor (no separate property-level field).
 *  3. "Calculate and generate demand for year(s)" — financial-year checkboxes (dynamic from the FY master); the rate
 *     engine runs once per selected year and the estimate lists every head per year (Arrear ₹0 for a new property).
 *  4. Satisfied → submit · Not satisfied → "I have a concern" + remarks → submit with concern.
 *  The SAF is auto-assigned to the Field Collector of the ward for physical verification, then Supervisor → Municipal Admin.
 *  On final approval the generated Property PIN is linked to this citizen's account automatically.
 */
const STEPS = [
  { n: 1, t: "Physical verification", d: "The Field Collector of your ward visits the property, checks every detail and enters the final demand." },
  { n: 2, t: "Supervisor review", d: "The Supervisor reviews the collector's verification and can modify or send it back." },
  { n: 3, t: "Final approval & PIN", d: "The Municipal Admin approves. Your Property PIN is generated and linked to this account — you can then pay online." },
];

/** Financial years newest first — current FY on top, then by start date descending. */
const sortFys = (list) => [...(list || [])].sort((a, b) => {
  if (!!a.is_current !== !!b.is_current) return a.is_current ? -1 : 1;
  return String(b.start_date || b.fy_label || "").localeCompare(String(a.start_date || a.fy_label || ""));
});

/** Phase 209 — "Calculate and generate demand for year(s)" checkbox box (dynamic FY master). */
function FinancialYearPicker({ fys, selected, onToggle, testIdPrefix = "saf-calcfy" }) {
  return (
    <Card className="shadow-sm" data-testid={`${testIdPrefix}-box`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2"><CalendarRange className="h-4 w-4" /> Calculate and generate demand for year(s)</CardTitle>
        <CardDescription>Tick the financial year(s) you want the demand generated for. Each year is calculated with its own rates and gets its own demand lines.</CardDescription>
      </CardHeader>
      <CardContent>
        {fys === null ? (
          <div className="flex gap-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9 w-28 rounded-md" />)}</div>
        ) : fys.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-empty`}>No financial years are configured yet — please contact the municipality.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {fys.map((f) => {
              const on = selected.includes(f.id);
              const id = `${testIdPrefix}-${f.id}`;
              return (
                <label key={f.id} htmlFor={id}
                  className={`flex items-center gap-2 border rounded-md px-3 h-10 text-sm cursor-pointer select-none transition-colors ${on ? "border-primary bg-accent text-accent-foreground" : "hover:bg-muted/60"}`}
                  data-testid={`${testIdPrefix}-option-${f.fy_label}`} data-state={on ? "checked" : "unchecked"}>
                  <Checkbox id={id} checked={on} onCheckedChange={() => onToggle(f.id)} data-testid={`${testIdPrefix}-check-${f.fy_label}`} aria-label={`FY ${f.fy_label}`} />
                  <span>FY {f.fy_label}{f.is_current ? <span className="text-[11px] text-muted-foreground"> (current)</span> : null}</span>
                </label>
              );
            })}
          </div>
        )}
        {fys && fys.length > 0 && (
          <p className="text-[11px] text-muted-foreground mt-2" data-testid={`${testIdPrefix}-count`}>
            {selected.length === 0 ? "Select at least one year to calculate the tax." : `${selected.length} year${selected.length > 1 ? "s" : ""} selected`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

const MasterSelect = ({ label, options, labelKey, value, onChange, testId, placeholder = "Select", required = false, disabled = false }) => {
  const sel = (options || []).find((o) => o.id === value);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}{required ? " *" : ""}</Label>
      <Select value={value || ""} onValueChange={(v) => v && onChange(v)} disabled={disabled}>
        <SelectTrigger className="h-10" data-testid={testId} aria-label={label}><SelectValue placeholder={placeholder}>{sel ? sel[labelKey] : null}</SelectValue></SelectTrigger>
        <SelectContent className="max-h-64">{(options || []).map((o) => <SelectItem key={o.id} value={o.id}>{o[labelKey]}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

function MySafCard({ code, saf, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const approved = saf.status === "approved";
  const finalTotal = saf.final_totals?.total || 0;
  return (
    <Card className={`shadow-sm ${approved ? "border-emerald-300" : saf.status === "rejected" ? "border-red-200" : ""}`} data-testid={`my-saf-${saf.saf_id}`} data-status={saf.status}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold" data-testid={`my-saf-number-${saf.saf_id}`}>{saf.saf_id}</p>
            <p className="text-sm truncate">{saf.owner_name}{saf.house_no ? ` · ${saf.house_no}` : ""}</p>
            <p className="text-xs text-muted-foreground truncate">{[saf.zone_name, saf.ward_name, saf.mohalla_name].filter(Boolean).join(" › ")}{saf.address ? ` · ${saf.address}` : ""}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">Submitted {fmtDateTime(saf.submitted_at)}</p>
          </div>
          <div className="text-right space-y-1 shrink-0">
            <SafStatusBadge status={saf.status} testId={`my-saf-status-${saf.saf_id}`} />
            <p className="text-xs text-muted-foreground tabular-nums">{approved ? `Final demand ${fmtINR(finalTotal)}` : `Estimated ${fmtINR(saf.estimate_total || 0)}`}</p>
          </div>
        </div>
        {/* Phase 210: routing / auto-escalation / direct decision tags (citizen-facing copy) */}
        <SafEscalationTags tags={(saf.escalation_tags || []).filter((t) => t.tone !== "routing")} testId={`my-saf-tags-${saf.saf_id}`} />

        {approved && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`my-saf-approved-${saf.saf_id}`}>
            <div>
              <p className="text-sm font-semibold text-emerald-900 flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Assessment Approved — Your PIN is <span className="font-mono" data-testid={`my-saf-pin-${saf.saf_id}`}>{saf.pin}</span></p>
              <p className="text-xs text-emerald-900/80 mt-0.5">The property is linked to your account. View the demand and pay online from My Properties.</p>
              {saf.direct_decision?.action === "approve" && (
                <p className="text-xs text-emerald-900/90 mt-1" data-testid={`my-saf-direct-${saf.saf_id}`}>
                  Approved directly by the {saf.direct_decision.by_role_label || "Municipal Admin"}{saf.direct_decision.reason ? <>: “{saf.direct_decision.reason}”</> : "."}
                </p>
              )}
            </div>
            <Button asChild size="sm" data-testid={`my-saf-pay-${saf.saf_id}`}><Link to={`/${code}/portal/properties`}>View & pay <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Link></Button>
          </div>
        )}
        {saf.status === "rejected" && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-testid={`my-saf-rejected-${saf.saf_id}`}>
            <p className="text-sm font-semibold text-red-900">Rejected by the {saf.direct_decision?.action === "reject" ? (saf.direct_decision.by_role_label || "Municipal Admin") : "municipality"}</p>
            <p className="text-sm text-red-900 mt-0.5" data-testid={`my-saf-rejection-reason-${saf.saf_id}`}>“{saf.rejection_remarks || saf.direct_decision?.reason || "No reason recorded"}”</p>
            <p className="text-xs text-red-900/80 mt-1">You may submit a corrected self-assessment.</p>
          </div>
        )}
        {!approved && saf.status !== "rejected" && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid={`my-saf-stage-${saf.saf_id}`}>
            <ClipboardList className="h-3.5 w-3.5" />
            {saf.status === "pending_verification" && (saf.assigned_collector_name ? `Field Collector ${saf.assigned_collector_name} will visit for physical verification.` : "Waiting for the municipality to assign a Field Collector.")}
            {saf.status === "sent_back" && "Under correction by the Field Collector after review."}
            {saf.status === "collector_verified" && (saf.skipped_collector ? "No Field Collector covers your ward — under the Supervisor's review." : saf.escalated_from_collector ? "Verification time limit passed — now with the Supervisor for review." : "Verified on site — waiting for the Supervisor's review.")}
            {saf.status === "supervisor_approved" && (saf.escalated_from_supervisor ? "Review time limit passed — now with the Municipal Admin for final approval." : saf.skipped_supervisor || (saf.skipped_collector && !saf.assigned_supervisor_id) ? "Under the Municipal Admin's direct review." : "Approved by the Supervisor — waiting for the Municipal Admin's final approval.")}
          </p>
        )}
        {saf.citizen_remarks && (
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2" data-testid={`my-saf-concern-${saf.saf_id}`}>
            <MessageSquareWarning className="h-3.5 w-3.5 inline mr-1" /> Your concern: “{saf.citizen_remarks}”
          </p>
        )}
        <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setOpen((v) => !v)} data-testid={`my-saf-toggle-${saf.saf_id}`}>
          {open ? "Hide details" : "Show estimate & progress"}
        </Button>
        {open && (
          <div className="space-y-3 pt-1" data-testid={`my-saf-details-${saf.saf_id}`}>
            {approved && saf.final_lines?.length > 0 && <SafTaxTable lines={saf.final_lines} caption="Approved demand" highlight testId={`my-saf-final-${saf.saf_id}`} />}
            <SafTaxEstimate estimate={saf.estimate} testId={`my-saf-estimate-${saf.saf_id}`} />
            <div>
              <p className="text-xs font-semibold mb-2">Progress</p>
              <SafTimeline history={saf.history || []} compact testId={`my-saf-timeline-${saf.saf_id}`} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SelfAssessment() {
  const { code } = useParams();
  const { auth } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const opts = useMasterOptions(code);
  const [tab, setTab] = useState(searchParams.get("tab") === "mine" ? "mine" : "new");
  const [form, setForm] = useState({ owner_name: auth?.user?.name || "", mobile: auth?.user?.mobile || "" });
  const [wardOpts, setWardOpts] = useState([]);
  const [mohallaOpts, setMohallaOpts] = useState([]);
  const [floors, setFloors] = useState([]);
  const [fys, setFys] = useState(null);              // Phase 209: financial-year master (null = loading)
  const [fyIds, setFyIds] = useState([]);            // Phase 209: years to calculate / generate demand for
  const [estimate, setEstimate] = useState(null);
  const [estimateKey, setEstimateKey] = useState(null);
  const [calcBusy, setCalcBusy] = useState(false);
  const [concern, setConcern] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [mine, setMine] = useState(null);

  // first floor row defaults to "Ground" once the floor master is known
  useEffect(() => { if (floors.length === 0 && opts.floors) setFloors([newSafFloor(0, opts)]); }, [opts, floors.length]);
  // Phase 209: financial years (newest first); the current FY is pre-selected
  useEffect(() => {
    api.get(`/t/${code}/masters/financial-years`).then((r) => {
      const list = sortFys(r.data);
      setFys(list);
      setFyIds((cur) => (cur.length ? cur : list.filter((f) => f.is_current).map((f) => f.id)));
    }).catch(() => setFys([]));
  }, [code]);
  const toggleFy = (id) => setFyIds((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const orderedFyIds = useMemo(() => (fys || []).filter((f) => fyIds.includes(f.id)).map((f) => f.id), [fys, fyIds]);
  useEffect(() => {
    if (form.zone_id) api.get(`/t/${code}/masters/wards`, { params: { parent_id: form.zone_id, include_inactive: false } }).then((r) => setWardOpts(r.data)).catch(() => {});
    else setWardOpts([]);
  }, [form.zone_id, code]);
  useEffect(() => {
    if (form.ward_id) api.get(`/t/${code}/masters/mohallas`, { params: { parent_id: form.ward_id, include_inactive: false } }).then((r) => setMohallaOpts(r.data)).catch(() => {});
    else setMohallaOpts([]);
  }, [form.ward_id, code]);
  const loadMine = () => api.get(`/t/${code}/saf/mine`).then((r) => setMine(r.data)).catch(() => setMine((m) => m || []));
  useEffect(() => { loadMine(); }, [code]);   // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setZone = (v) => setForm((f) => ({ ...f, zone_id: v, ward_id: "", mohalla_id: "" }));
  const setWard = (v) => setForm((f) => ({ ...f, ward_id: v, mohalla_id: "" }));

  const payloadFloors = useMemo(() => floorsPayload(floors), [floors]);
  const payloadKey = useMemo(() => JSON.stringify({ rw: form.road_width_id || "", fys: orderedFyIds, floors: payloadFloors }), [form.road_width_id, orderedFyIds, payloadFloors]);
  const stale = !!estimate && estimateKey !== payloadKey;
  const floorsReady = payloadFloors.some((f) => f.usage_type_id && (f.constructed_area_sqft > 0 || f.carpet_area_sqft > 0));

  const validate = (forSubmit) => {
    if (!(form.owner_name || "").trim()) return "Owner name is required";
    if (forSubmit && (!form.zone_id || !form.ward_id)) return "Select your Zone and Ward";
    if (!form.road_width_id) return "Select the road width in front of the property — the tax rate depends on it";
    if (!floorsReady) return "Add at least one floor with a usage type and a constructed / carpet area";
    const missing = floors.filter(isFloorFilled).find((f) => !f.usage_type_id || !f.const_type_id);
    if (missing) return `Pick the usage and construction type for ${missing.floor_label || "every floor"}`;
    if (fys && fys.length > 0 && orderedFyIds.length === 0) return "Tick at least one financial year under \"Calculate and generate demand for year(s)\"";
    return null;
  };

  const calculate = async () => {
    const err = validate(false);
    if (err) return toast.error(err);
    setCalcBusy(true);
    try {
      const r = await api.post(`/t/${code}/saf/estimate`, { road_width_id: form.road_width_id, owner_name: form.owner_name, floors: payloadFloors, financial_year_ids: orderedFyIds });
      setEstimate(r.data);
      setEstimateKey(payloadKey);
      setConcern(false);
      const labels = (r.data.fy_labels || [r.data.fy_label]).filter(Boolean);
      toast.success(`Estimated tax ${fmtINR(r.data.total_demand)} for FY ${labels.join(", ")}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setCalcBusy(false); }
  };

  const submit = async (withConcern) => {
    const err = validate(true);
    if (err) return toast.error(err);
    if (!estimate || stale) return toast.error("Please calculate the tax for the current details first");
    if (withConcern && remarks.trim().length < 5) return toast.error("Please describe your concern (at least 5 characters) — it is shown to the Field Collector");
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/saf`, {
        owner_name: form.owner_name.trim(), father_spouse_name: form.father_spouse_name || null, mobile: form.mobile || null,
        email: auth?.user?.email || null, house_no: form.house_no || null, address: form.address || null,
        zone_id: form.zone_id, ward_id: form.ward_id, mohalla_id: form.mohalla_id || null, road_width_id: form.road_width_id,
        floors: payloadFloors, financial_year_ids: orderedFyIds, concern: !!withConcern, citizen_remarks: withConcern ? remarks.trim() : null,
      });
      setSubmitted(r.data);
      toast.success(`Self-assessment ${r.data.saf_id} submitted`);
      loadMine();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reset = () => {
    setSubmitted(null); setEstimate(null); setEstimateKey(null); setConcern(false); setRemarks("");
    setForm({ owner_name: auth?.user?.name || "", mobile: auth?.user?.mobile || "" }); setFloors([newSafFloor(0, opts)]);
    setFyIds((fys || []).filter((f) => f.is_current).map((f) => f.id));
  };
  const switchTab = (t) => { setTab(t); setSearchParams(t === "mine" ? { tab: "mine" } : {}, { replace: true }); };

  return (
    <div className="space-y-6" data-testid="citizen-saf-page">
      <div>
        <h1 className="text-2xl font-semibold font-heading">Self Assessment (SAF)</h1>
        <p className="text-muted-foreground text-sm">Declare your property floor by floor, see the estimated tax instantly and submit. The municipality verifies it on site and issues your Property PIN.</p>
      </div>

      <Tabs value={tab} onValueChange={switchTab}>
        <TabsList>
          <TabsTrigger value="new" data-testid="saf-tab-new"><FileSignature className="h-3.5 w-3.5 mr-1.5" /> New self-assessment</TabsTrigger>
          <TabsTrigger value="mine" data-testid="saf-tab-mine"><ClipboardList className="h-3.5 w-3.5 mr-1.5" /> My submissions{mine ? ` (${mine.length})` : ""}</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4">
          {submitted ? (
            <Card className="shadow-sm border-emerald-300" data-testid="saf-success-panel">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><CheckCircle2 className="h-6 w-6" /></div>
                  <div>
                    <p className="font-semibold text-lg font-heading">Self-assessment submitted</p>
                    <p className="text-sm text-muted-foreground">Reference <span className="font-mono font-semibold text-foreground" data-testid="saf-success-number">{submitted.saf_id}</span> · <SafStatusBadge status={submitted.status} testId="saf-success-status" /></p>
                    <p className="text-sm mt-1" data-testid="saf-success-assignment">
                      {submitted.assigned_collector_name
                        ? <>Assigned to Field Collector <b>{submitted.assigned_collector_name}</b> for physical verification.</>
                        : "The municipality will assign a Field Collector for physical verification."}
                      {submitted.citizen_remarks ? " Your concern has been recorded and will be addressed during the visit." : ""}
                    </p>
                  </div>
                </div>
                <ol className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {STEPS.map((s) => (
                    <li key={s.n} className="border rounded-lg p-3 bg-muted/30">
                      <p className="text-xs font-semibold text-primary">Step {s.n}</p>
                      <p className="text-sm font-medium">{s.t}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.d}</p>
                    </li>
                  ))}
                </ol>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => switchTab("mine")} data-testid="saf-success-view-mine"><ClipboardList className="h-4 w-4 mr-2" /> Track my submissions</Button>
                  <Button asChild variant="outline" data-testid="saf-success-home"><Link to={`/${code}/portal`}><Home className="h-4 w-4 mr-2" /> Back to dashboard</Link></Button>
                  <Button variant="ghost" onClick={reset} data-testid="saf-success-new">Submit another property</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
              <div className="xl:col-span-2 space-y-4">
                <Card className="shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading">Property details</CardTitle>
                    <CardDescription>Owner and location of the property you are declaring. Fields marked * are required.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="space-y-1"><Label className="text-xs">Owner name *</Label><Input className="h-10" value={form.owner_name || ""} onChange={(e) => set("owner_name", e.target.value)} data-testid="saf-owner-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Father / Spouse name</Label><Input className="h-10" value={form.father_spouse_name || ""} onChange={(e) => set("father_spouse_name", e.target.value)} data-testid="saf-father-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">Mobile</Label><Input className="h-10" inputMode="tel" value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} data-testid="saf-mobile-input" /></div>
                      <div className="space-y-1"><Label className="text-xs">House No</Label><Input className="h-10" value={form.house_no || ""} onChange={(e) => set("house_no", e.target.value)} data-testid="saf-house-input" /></div>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Address</Label><Textarea rows={2} value={form.address || ""} onChange={(e) => set("address", e.target.value)} data-testid="saf-address-input" /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <MasterSelect label="Zone" required options={opts.zones} labelKey="zone_name" value={form.zone_id} onChange={setZone} testId="saf-zone-select" />
                      <MasterSelect label="Ward" required options={wardOpts} labelKey="ward_name" value={form.ward_id} onChange={setWard} testId="saf-ward-select" disabled={!form.zone_id} placeholder={form.zone_id ? "Select" : "Pick zone first"} />
                      <MasterSelect label="Mohalla" options={mohallaOpts} labelKey="mohalla_name" value={form.mohalla_id} onChange={(v) => set("mohalla_id", v)} testId="saf-mohalla-select" disabled={!form.ward_id} placeholder={form.ward_id ? "Select" : "Pick ward first"} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <MasterSelect label="Road width in front of the property" required options={opts["road-widths"]} labelKey="width_label" value={form.road_width_id} onChange={(v) => set("road_width_id", v)} testId="saf-road-width-select" />
                      <p className="text-[11px] text-muted-foreground self-end pb-2" data-testid="saf-ptype-hint">Property type, construction type and usage are declared per floor in the section below.</p>
                    </div>
                  </CardContent>
                </Card>

                {floors.length === 0 ? <Skeleton className="h-40 rounded-xl" /> : (
                  <SafFloorEditor masters={opts} floors={floors} setFloors={setFloors} testIdPrefix="saf-floor"
                    title="Floor-wise assessment" description="Add one row per floor — the same details as the municipality's Manual Entry: Floor, House No, Property type, Construction type, Usage, Sub-usage, Category, Sub-category, areas in sq ft, Electricity CA No and Construction date (age is worked out automatically). Constructed area auto-fills as Plot − Open and carpet area as 80% of constructed; both can be edited." />
                )}

                <FinancialYearPicker fys={fys} selected={fyIds} onToggle={toggleFy} />
              </div>

              <div className="space-y-4 xl:sticky xl:top-20 h-fit">
                <Card className="shadow-sm border-primary/30" data-testid="saf-estimate-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2"><Calculator className="h-4 w-4" /> Estimated tax</CardTitle>
                    <CardDescription>Head-wise estimate from the municipality's rate engine for the financial year(s) you ticked. A new property has no arrears.</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button variant={estimate && !stale ? "outline" : "default"} className="w-full h-11" onClick={calculate} disabled={calcBusy} data-testid="saf-calculate-button">
                      <Calculator className="h-4 w-4 mr-2" /> {calcBusy ? "Calculating…" : estimate ? "Recalculate tax" : "Calculate Tax"}
                    </Button>
                    {stale && (
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-start gap-1.5" data-testid="saf-estimate-stale">
                        <TriangleAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" /> You changed the floors, road width or the selected year(s) — recalculate before submitting.
                      </p>
                    )}
                    {estimate ? <SafTaxEstimate estimate={estimate} testId="saf-estimate" /> : (
                      <p className="text-xs text-muted-foreground text-center py-3" data-testid="saf-estimate-empty">Fill the property details and floors, tick the year(s), then click Calculate Tax.</p>
                    )}
                  </CardContent>
                </Card>

                {estimate && !stale && (
                  <Card className="shadow-sm" data-testid="saf-decision-card">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-heading">Are you satisfied with this estimate?</CardTitle>
                      <CardDescription>Either way your self-assessment goes to the Field Collector for physical verification. The final demand is fixed after the visit.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {!concern ? (
                        <div className="grid grid-cols-1 gap-2">
                          <Button className="w-full h-11" onClick={() => submit(false)} disabled={busy} data-testid="saf-submit-satisfied">
                            <ThumbsUp className="h-4 w-4 mr-2" /> {busy ? "Submitting…" : "Yes — Submit Self-Assessment"}
                          </Button>
                          <Button variant="outline" className="w-full h-11 border-amber-300 text-amber-900 hover:bg-amber-50" onClick={() => setConcern(true)} disabled={busy} data-testid="saf-not-satisfied">
                            <ThumbsDown className="h-4 w-4 mr-2" /> No — I have a concern
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid="saf-concern-box">
                          <Label className="text-xs flex items-center gap-1.5"><MessageSquareWarning className="h-3.5 w-3.5" /> Describe your concern *</Label>
                          <Textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="saf-concern-remarks"
                            placeholder="e.g. The estimate seems high — the first floor is only partly constructed; the road in front is narrower than 30 ft…" />
                          <p className="text-[11px] text-muted-foreground">Your remarks are shown to the Field Collector, Supervisor and Municipal Admin and kept in the approval log.</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button className="w-full h-11" onClick={() => submit(true)} disabled={busy} data-testid="saf-submit-concern">
                              <Send className="h-4 w-4 mr-2" /> {busy ? "Submitting…" : "Submit with my concern"}
                            </Button>
                            <Button variant="ghost" className="w-full h-11" onClick={() => { setConcern(false); setRemarks(""); }} disabled={busy} data-testid="saf-concern-cancel">Back</Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="mine" className="mt-4">
          {mine === null ? (
            <div className="space-y-3" data-testid="my-saf-skeleton">{[0, 1].map((i) => <Skeleton key={i} className="h-32 rounded-xl" />)}</div>
          ) : mine.length === 0 ? (
            <Card className="shadow-sm border-dashed" data-testid="my-saf-empty">
              <CardContent className="p-6 text-center space-y-2">
                <div className="h-11 w-11 rounded-xl bg-accent mx-auto flex items-center justify-center"><FileSignature className="h-5 w-5 text-accent-foreground" /></div>
                <p className="font-medium">No self-assessments yet</p>
                <p className="text-sm text-muted-foreground">Declare your property and get an instant tax estimate — the municipality verifies it and issues your PIN.</p>
                <Button onClick={() => switchTab("new")} data-testid="my-saf-start">Start a self-assessment <ArrowRight className="h-4 w-4 ml-2" /></Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3" data-testid="my-saf-list">
              {mine.map((s, i) => <MySafCard key={s.id} code={code} saf={s} defaultOpen={i === 0 && mine.length === 1} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
